/* ==========================================================================
   MEGAKRUNCH — Lazy autoplay + a11y controls for mk-* section videos.

   Sections (#44) render <video preload="none"> without an autoplay attribute,
   plus a `.mk-lazy-video` class. This script:

     1. IntersectionObservers them and calls .play() only when the video is
        within ~200px of the viewport. Off-screen videos stay unloaded (#44).

     2. Injects a pause/play button overlay onto each video so customers can
        stop autoplay (WCAG 2.2.2 — moving content >5s must be pausable). The
        button is keyboard-accessible (Enter/Space toggles) and aria-labeled
        with the current state ("Pause video" / "Play video") (Phase 4 / #22).

     3. Marks each <video> as aria-hidden="true" — these are decorative
        looping demos with no narration; sighted users see the demo, SR users
        don't need an audio description of every frame (Phase 4 / #35). The
        pause button is exposed to AT so motor-impaired users can stop motion.

     4. Honors prefers-reduced-motion: reduce — does NOT autoplay, leaves the
        first frame visible as a poster (Phase 4 / #23 video bits). Customer
        can still hit the play button if they want the demo.

   `.card__hover-video` on collection cards is NOT observed — those play on
   hover, not autoplay, and don't qualify for WCAG 2.2.2 (no autoplay).
   ========================================================================== */

(function () {
  if (typeof IntersectionObserver === 'undefined') return;

  var videos = document.querySelectorAll('.mk-lazy-video');
  if (!videos.length) return;

  // Phase 4 / #23 — Detect once at init. We don't react to changes mid-page
  // because the autoplay decision is made per-video when it enters the
  // viewport; if a customer toggles their OS setting mid-session, page
  // reload reflects the new preference.
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setButtonState(btn, isPlaying) {
    btn.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    btn.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    btn.classList.toggle('is-playing', isPlaying);
  }

  function injectControls(video) {
    // Mark the video itself as decorative — descriptive content lives in
    // the section's headings/text. The pause button is the AT-exposed
    // control surface.
    video.setAttribute('aria-hidden', 'true');
    // Disable native context menu / picture-in-picture etc. since the
    // videos are meant as ambient demos, not first-class media. Belt-and-
    // suspenders alongside the existing .product-media-container CSS that
    // hides controls.
    video.setAttribute('tabindex', '-1');

    // The video's parent must be position: relative for the absolutely-
    // positioned overlay button to anchor correctly. Most consumer
    // sections wrap the video in a positioned container already; if not,
    // we add position: relative inline so we don't depend on a CSS round
    // trip.
    var parent = video.parentElement;
    if (parent && getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'mk-video-pause-toggle';
    btn.innerHTML =
      '<svg class="mk-video-pause-toggle__icon mk-video-pause-toggle__icon--pause" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>' +
      '<svg class="mk-video-pause-toggle__icon mk-video-pause-toggle__icon--play" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"/></svg>';
    setButtonState(btn, !reducedMotion); // assume autoplay unless reduced-motion

    btn.addEventListener('click', function () {
      if (video.paused) {
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
        setButtonState(btn, true);
      } else {
        video.pause();
        setButtonState(btn, false);
      }
    });

    // Sync button state when video state changes for any other reason
    // (e.g. IntersectionObserver pausing on scroll-out — without this,
    // a customer who scrolled past a paused video would see a "Pause"
    // label on a paused video).
    video.addEventListener('play', function () { setButtonState(btn, true); });
    video.addEventListener('pause', function () { setButtonState(btn, false); });

    parent.appendChild(btn);
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.isIntersecting) {
        // Phase 4 / #23 — Don't autoplay under reduced-motion. The video
        // is still in the DOM with its first frame visible (since the
        // browser fetches metadata on intersection); the customer can
        // press the pause/play button to opt in.
        if (reducedMotion) {
          if (video.preload === 'none') video.preload = 'metadata';
          return;
        }
        // Compare against 'auto' rather than 'none': mk-responsive-video now
        // renders these with preload="metadata" so the container header is
        // parsed before the slide is reached. An equality check on 'none'
        // would silently never upgrade them, leaving playback to stall on a
        // metadata-only buffer.
        if (video.preload !== 'auto') {
          video.preload = 'auto';
        }
        var p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      } else {
        if (!video.paused) video.pause();
      }
    });
  }, {
    rootMargin: '200px 0px',
    threshold: 0.1
  });

  videos.forEach(function (v) {
    injectControls(v);
    observer.observe(v);
  });
})();
